@if (isMobile && mobileCartOpen) {
  <div
    class="cart-backdrop"
    (click)="toggleMobileCart()"
  ></div>
}

<div
  class="cart-container"
  [ngClass]="{
    'mobile-overlay': isMobile,
    'mobile-open': isMobile && mobileCartOpen,
    'no-transition': !initialized,
  }"
  >
  <!-- === HEADER === -->
  <div class="cart-header">
    <h3>Basket</h3>
    <button class="close-cart-btn" (click)="toggleMobileCart()">
      <i class="fas fa-times"></i>
    </button>
  </div>

  <!-- SCROLLABLE CONTENT -->
  @if (cartItems.length > 0) {
    <div class="cart-content">
      <div class="basket-mode-toggle">
        <button
          type="button"
          [class.active]="orderType === 'collection'"
          (click)="changeOrderType('collection')"
          >
          Collection
        </button>
        <button
          type="button"
          [class.active]="orderType === 'delivery'"
          [class.is-unavailable]="deliveryDisabled"
          (click)="changeOrderType('delivery')"
          >
          Delivery
          @if (deliveryDisabled && deliveryToggleLabel) {
            <span class="meta">
              ({{ deliveryToggleLabel }})
            </span>
          }
        </button>
      </div>
      <!-- BASKET SECTION -->
      <div class="section-wrapper basket-wrapper">
        <h4 class="section-title">Basket</h4>
        <div class="section-box basket-section">
          @for (item of cartItems; track item) {
            <div class="cart-item">
              <div class="cart-details">
                <div class="item-top">
                  <h4>{{ item.name }}</h4>
                  <p class="item-price">£{{ item.price.toFixed(2) }}</p>
                </div>
                @if (item.extras && item.extras.length > 0) {
                  <ul
                    class="extras-list"
                    >
                    @for (extra of item.extras; track extra) {
                      <li>
                        + {{ extra.choice }}
                        @if (extra.price > 0) {
                          <span
                            >({{ extra.price | currency: "GBP" }})</span
                            >
                          }
                        </li>
                      }
                    </ul>
                  }
                  <div class="quantity-controls">
                    <button (click)="updateQuantity(item, -1)">-</button>
                    <span>{{ item.quantity }}</span>
                    <button (click)="updateQuantity(item, 1)">+</button>
                  </div>
                </div>
                <button class="remove-btn" (click)="removeItem(item.id)">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            }
            <div class="basket-total">
              <span>Basket total</span>
              <span>{{ getSubtotal() | currency: "GBP" }}</span>
            </div>
          </div>
        </div>
        <!-- NOTES SECTION -->
        <div class="notes-section" (click)="openNotesModal()">
          <div class="notes-header">
            <h4 class="section-title">Notes for restaurant</h4>
            <button class="notes-add-btn"><i class="fas fa-plus"></i></button>
          </div>
          @if (!orderNotes) {
            <p class="no-notes">No notes included</p>
          }
          @if (orderNotes) {
            <p class="has-notes">{{ orderNotes }}</p>
          }
        </div>
        <!-- FEES SECTION -->
        <div class="section-wrapper fees-wrapper">
          <div class="section-header">
            <h4 class="section-title">Fees</h4>
            <i
              class="fas fa-info-circle tooltip-icon"
              (click)="openFeesModal()"
              title="Why these fees?"
            ></i>
          </div>
          <div class="section-box fees-section">
            @if (isDelivery) {
              <div class="fee-row">
                <span>Delivery fee</span>
                <span>{{ deliveryFeeOriginalGross | currency: "GBP" }}</span>
              </div>
            }
            @if (getSmallOrderFee() > 0) {
              <div class="fee-row">
                <span>Small-order fee</span>
                <span>{{ getSmallOrderFee() | currency: "GBP" }}</span>
              </div>
            }
            <div class="fee-row">
              <span>Service fee</span>
              <span>{{ serviceFee | currency: "GBP" }}</span>
            </div>
          </div>
        </div>
      </div>
    } @else {
      <div class="empty-cart">
        <i class="empty-icon fa-solid fa-cart-shopping"></i>
        <p class="empty-message">Your basket is empty</p>
      </div>
    }
    <!-- Empty State -->

    <!-- === SLIM FOOTER === -->
    @if (cartItems.length > 0) {
      <div class="cart-footer">
        <!-- Total -->
        <div class="total-row">
          <span>Total</span>
          <span>{{ getTotal() | currency: "GBP" }}</span>
        </div>
        @if (isBelowMinimum) {
          <p class="minimum-warning">
            {{ minimumOrderMessage }}
          </p>
        }
        <div class="footer-buttons">
          <button
            class="checkout-btn btn-primary"
            (click)="proceedToCheckout()"
            [disabled]="!canProceedToCheckout"
            >
            Proceed to checkout
          </button>
        </div>
      </div>
    }
  </div>

  <!-- 📱 Floating cart button (mobile only) -->
  @if (isMobile && !mobileCartOpen && cartItems.length > 0) {
    <div
      class="mobile-cart-bar"
      >
      <button class="mobile-cart-toggle btn-primary" (click)="toggleMobileCart()">
        View Basket &nbsp; • &nbsp; £{{ getSubtotal().toFixed(2) }}
      </button>
    </div>
  }

  <!-- Fees Explained -->
  <axis-modal [show]="showFeesModal" (closed)="closeFeesModal()">
    <div modal-header class="info-modal__header">
      <h2 class="info-modal__title">Why these fees?</h2>
      <button
        type="button"
        aria-label="Close modal"
        class="info-modal__close"
        (click)="closeFeesModal()"
        >
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>

    <div class="info-modal__body">
      @for (line of getFeesExplanationLines(); track line) {
        <p>{{ line }}</p>
      }
    </div>

    <div modal-footer class="info-modal__footer">
      <button class="btn-primary" (click)="closeFeesModal()">Got it</button>
    </div>
  </axis-modal>

  <!-- — Notes Modal — -->
  <axis-modal [show]="showNotesModal" (closed)="closeNotesModal()">
    <!-- HEADER -->
    <div modal-header class="info-modal__header">
      <h2 class="info-modal__title">Add a note</h2>

      <button
        type="button"
        class="info-modal__close"
        aria-label="Close information modal"
        (click)="closeNotesModal()"
        >
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>

    <!-- BODY -->
    <div class="info-modal__body">
      <textarea
        [(ngModel)]="orderNotes"
        placeholder="Enter any special instructions…"
      ></textarea>
    </div>

    <!-- FOOTER -->
    <div modal-footer class="info-modal__footer">
      <button class="btn-primary" (click)="saveNotes()">Save</button>
    </div>
  </axis-modal>
